import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { GroupContract } from '@isophi/teachers-shared';
import { ListResponseContract } from '@isophi/teachers-shared';
import { Group } from '@isophi/teachers-shared';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { TimestampObservableCache } from '../utils/timestamp-observable-cache';

@Injectable({
  providedIn: 'root',
})
export class GroupService {
  protected static readonly CACHE_EXPIRATION: number = 3600; // in seconds

  protected groupsCache: TimestampObservableCache<Group[]> | null = null;

  protected reloadGroupsSubject$ = new BehaviorSubject<void>(null);

  public constructor(private httpClient: HttpClient) {}

  /**
   * Return list of children groups.
   */
  public get groups$(): Observable<Group[]> {
    const url = `${environment.teacherAPI}/children-groups/`;

    if (this.groupsCache === null || this.groupsCache.isExpired()) {
      const groups$ = this.reloadGroupsSubject$.pipe(
        switchMap(() => {
          return this.httpClient.get<ListResponseContract<GroupContract>>(url).pipe(
            map((response) => response.results.map((groupData) => Group.deserialize(groupData))),
            catchError((error) => {
              this.groupsCache = null;
              return throwError(() => error);
            })
          )
        }),
        shareReplay(1)
      );

      this.groupsCache = new TimestampObservableCache<Group[]>(groups$, GroupService.CACHE_EXPIRATION);
    }

    return this.groupsCache.observable;
  }

  public create(group: Group): Observable<GroupContract> {
    const url = `${environment.teacherAPI}/children-groups/`;

    this.invalidateCache();
    return this.httpClient.post<GroupContract>(url, group.serialize());
  }

  public update(group: Group): Observable<GroupContract> {
    const url = `${environment.teacherAPI}/children-groups/${group.uuid}/`;

    this.invalidateCache();
    return this.httpClient.patch<GroupContract>(url, group.serialize());
  }

  public delete(group: Group): Observable<void> {
    const url = `${environment.teacherAPI}/children-groups/${group.uuid}/`;

    this.invalidateCache();
    return this.httpClient.delete<void>(url);
  }

  /**
   * Take groups and sort it by name (inplace)
   */
  public sort(groups: Group[]): void {
    groups.sort((a, b) => a.name.localeCompare(b.name));
  }

  /**
   * Invalidate groups cache.
   */
  public invalidateCache(): void {
    this.groupsCache = null;
  }

  /**
   * Reload the groups by emitting a reload signal.
   */
  public reloadGroups(): void {
    this.reloadGroupsSubject$.next();
  }
}
